import useLogbogApi from "../../core/hooks/useLogbogApi";
import {useCallback, useEffect, useRef, useState} from "react";
import {DashboardFilterListModel, DashboardFilterModel} from "./models/DashboardFilter";
import {useDashboardContext} from "./dashboardContextProvider";
import {DashboardConstants} from "./dashboardConstants";
import {AsyncListSortOrderInput} from "../../core/sharedmodels/asyncList/asyncListSortOrderInput";
import {nameof} from "ts-simple-nameof";
import {DashboardListForloebModel} from "./dashboardList/models/DashboardListForloebModel";
import {AsyncListInput} from "../../core/sharedmodels/asyncList/asyncListInput";
import useUser from "../../core/hooks/useUser";
import {RolleTitelEnum} from "../../infrastructure/enums/rolleTitelEnum";

const filterInitialValue: DashboardFilterModel = {
    specialeIds: [],
    uddannelsesstedIds: [],
    uddannelsestrinIds: [],
    includeOrlov: false
}

const commonFilterValues = {
    draw: 0, // (pageindex) is 1, according to our pagination component
    start: 0,
    length: 10,
    search: {
        value: "",
    },
    order: [{ columnName: nameof<DashboardListForloebModel>(x => x.brugernavn).toLowerCase(), dir: "asc"  } as AsyncListSortOrderInput],
    grouping: [],
} as AsyncListInput;

const tableFilterInitialValue: DashboardFilterListModel = {
    dashboardFilterModel: filterInitialValue,
    ...commonFilterValues
}

const useDashboardPage = () => {
    const {dashboardApi} = useLogbogApi()
    const {
        setDashboardListData,
        setDashboardListUkoData,
        setDashboardListDataIsLoading,
        setUddannelseslaeger,
        setUddannelseslaegerIsLoading,
        setVejledersamtaler,
        setVejledersamtalerIsLoading,
        setUddannelsesplaner,
        setUddannelsesplanerIsLoading,
        setKompetenceProgress,
        setKompetenceProgressIsLoading,
        setKompetenceAktivitet,
        setKompetenceAktivitetIsLoading,
        setKompetenceKlarmeldinger,
        setKompetenceKlarmeldingerIsLoading,
        setVejledning,
        setVejledningIsLoading,
        setForloebUdenGodkendelse,
        setForloebUdenGodkendelseIsLoading
    } = useDashboardContext();

    const [filter, setFilter] = useState<DashboardFilterModel>(filterInitialValue);
    const [tableFilter, setTableFilter] = useState<DashboardFilterListModel>(tableFilterInitialValue); //Will be used if we go asyncronious with the table
    const {currentUser} = useUser();

    const initialFetchRef = useRef(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const delayBeforeFilterApply = DashboardConstants.FilterTimer;

    const getDashboardListData = useCallback(async () => {
        const currentTableFilter: DashboardFilterListModel = {
            dashboardFilterModel: filter,
            ...commonFilterValues
        }
        setDashboardListDataIsLoading(true);

        if (currentUser.hasRolleTitel(RolleTitelEnum.Uko)) {
            const dashboardListData = await dashboardApi.getDashboardForloebForUkoTable(currentTableFilter);
            setDashboardListUkoData(dashboardListData);
        } else {
            const dashboardListData = await dashboardApi.getDashboardForloebForTable(currentTableFilter);
            setDashboardListData(dashboardListData);
        }
        setDashboardListDataIsLoading(false);
    }, [currentUser, dashboardApi, filter, setDashboardListData, setDashboardListDataIsLoading, setDashboardListUkoData]);

    const getUddannelseslaeger = useCallback(async () => {
        setUddannelseslaegerIsLoading(true);
        const uddannelseslaeger = await dashboardApi.getDashboardUddannelseslaeger(filter);
        setUddannelseslaeger(uddannelseslaeger);
        setUddannelseslaegerIsLoading(false);
    }, [dashboardApi, filter, setUddannelseslaeger, setUddannelseslaegerIsLoading]);

    const getVejledersamtaler = useCallback(async () => {
        setVejledersamtalerIsLoading(true)
        const vejledersamtaler = await dashboardApi.getVejledersamtaler(filter);
        setVejledersamtaler(vejledersamtaler);
        setVejledersamtalerIsLoading(false);
    }, [dashboardApi, filter, setVejledersamtaler, setVejledersamtalerIsLoading]);

    const getUddannelsesplaner = useCallback(async () => {
        setUddannelsesplanerIsLoading(true)
        const uddannelsesplaner = await dashboardApi.getUddannelsesplaner(filter);
        setUddannelsesplaner(uddannelsesplaner);
        setUddannelsesplanerIsLoading(false);
    }, [dashboardApi, filter, setUddannelsesplaner, setUddannelsesplanerIsLoading]);

    const getKompetenceProgress = useCallback(async () => {
        setKompetenceProgressIsLoading(true);
        const kompetenceProgress = await dashboardApi.getKompetenceProgress(filter);
        setKompetenceProgress(kompetenceProgress);
        setKompetenceProgressIsLoading(false);
    }, [dashboardApi, filter, setKompetenceProgress, setKompetenceProgressIsLoading]);

    const getKompetenceAktivitet = useCallback(async () => {
        setKompetenceAktivitetIsLoading(true);
        const kompetenceAktivitet = await dashboardApi.getKompetenceAktivitet(filter);
        setKompetenceAktivitet(kompetenceAktivitet);
        setKompetenceAktivitetIsLoading(false);
    }, [dashboardApi, filter, setKompetenceAktivitet, setKompetenceAktivitetIsLoading]);

    const getKompetenceKlarmeldinger = useCallback(async () => {
        setKompetenceKlarmeldingerIsLoading(true);
        const kompetenceAktivitet = await dashboardApi.getOpmaerksomhedspunktKompetenceKlarmeldinger(filter);
        setKompetenceKlarmeldinger(kompetenceAktivitet);
        setKompetenceKlarmeldingerIsLoading(false);
    }, [dashboardApi, filter, setKompetenceKlarmeldinger, setKompetenceKlarmeldingerIsLoading]);

    const getVejledning = useCallback(async () => {
        setVejledningIsLoading(true);
        const vejledning = await dashboardApi.getOpmaerksomhedspunktVejledning(filter);
        setVejledning(vejledning);
        setVejledningIsLoading(false);
    }, [dashboardApi, filter, setVejledning, setVejledningIsLoading]);

    const getForloebUdenGodkendelse = useCallback(async () => {
        setForloebUdenGodkendelseIsLoading(true);
        const gamleForloebUdenGodkendelse = await dashboardApi.getForloebUdenGodkendelse(filter);
        setForloebUdenGodkendelse(gamleForloebUdenGodkendelse);
        setForloebUdenGodkendelseIsLoading(false);
    }, [dashboardApi, filter, setForloebUdenGodkendelse, setForloebUdenGodkendelseIsLoading]);

    useEffect(() => {
        const fetch = async () => {
            await Promise.all([
                getDashboardListData(),
                getUddannelseslaeger(),
                getVejledersamtaler(),
                getUddannelsesplaner(),
                getKompetenceProgress(),
                getKompetenceAktivitet(),
                getKompetenceKlarmeldinger(),
                getVejledning(),
                getForloebUdenGodkendelse(),
            ])
        };

        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(async () => {
            fetch().then(() => initialFetchRef.current = false);
        }, initialFetchRef.current ? 0 : delayBeforeFilterApply);
    }, [delayBeforeFilterApply, getDashboardListData, getForloebUdenGodkendelse, getKompetenceAktivitet, getKompetenceKlarmeldinger, getKompetenceProgress, getUddannelseslaeger, getUddannelsesplaner, getVejledersamtaler, getVejledning])

    return {
        filter: filter,
        setFilter: setFilter,
        tableFilter: tableFilter,
        initialFetchRef: initialFetchRef,
    }
}
export default useDashboardPage;
